<template>
  <Modal
    v-model="dialogVisible"
    class-name="bp-sync-fail-dialog"
    :title="dialogTitle"
    width="980"
    :mask-closable="false"
    @on-cancel="handleClose"
  >
    <div class="bp-sync-fail-body">
      <div class="bp-sync-fail-table-wrap">
        <Table
          class="bp-sync-fail-table"
          :columns="columns"
          :data="failItemList"
          :width="tableTotalWidth"
          v-bind="tableScrollBinding"
          :loading="loading"
          no-data-text="暂无同步失败明细"
        />
      </div>

      <div class="bp-sync-fail-time">
        最近失败时间：{{ failTimeText }}
      </div>
      <p class="bp-sync-fail-tip">
        内容素材问题请在内容中心修正后重新推送；系统或接口异常由开发处理。
      </p>
    </div>

    <div slot="footer">
      <Button @click="handleClose">关闭</Button>
    </div>
  </Modal>
</template>

<script>
import dayjs from 'dayjs'
import { getTtMediaReviewSyncFailures } from '@/api/short-review'

/**
 * - 纵向：条数 ≤ MAX_ROWS_BEFORE_SCROLL 时表格随内容增高；超出后固定表体高度 TABLE_SCROLL_HEIGHT 并滚动
 * - 横向：列宽之和 TABLE_TOTAL_WIDTH 大于弹窗内表格区域（约 932px）即出现横向滚动
 */
const TABLE_SCROLL_HEIGHT = 320
const TABLE_ROW_HEIGHT = 48
/** 不超过此行数时不设 height，避免少量数据时出现大块空白 */
const MAX_ROWS_BEFORE_SCROLL = Math.floor(TABLE_SCROLL_HEIGHT / TABLE_ROW_HEIGHT)
const TABLE_COLUMN_CONTENT_WIDTH = 200
const TABLE_COLUMN_REASON_WIDTH = 820
const TABLE_TOTAL_WIDTH =
  TABLE_COLUMN_CONTENT_WIDTH + TABLE_COLUMN_REASON_WIDTH

/** 接口 TtMediaIssueVO → 表格行；失败内容仅展示集号 */
function mapSyncFailureIssue(issue) {
  if (!issue) {
    return { content: '—', reason: '—' }
  }
  const num = issue.seriesNum
  const hasEpisode =
    num != null && num !== '' && !Number.isNaN(Number(num)) && Number(num) > 0
  return {
    content: hasEpisode ? `第${Number(num)}集` : '整剧',
    reason: (issue.reason && String(issue.reason).trim()) || '—'
  }
}

function pickLatestOccurredAt(issues) {
  let latest = null
  ;(issues || []).forEach((item) => {
    if (!item || !item.occurredAt) return
    const parsed = dayjs(item.occurredAt)
    if (!parsed.isValid()) return
    if (!latest || parsed.isAfter(latest)) {
      latest = parsed
    }
  })
  return latest
}

export default {
  name: 'BpSyncFailDialog',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    row: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      dialogVisible: this.visible,
      loading: false,
      failItemList: [],
      latestFailTimeText: '',
      columns: [
        {
          title: '失败内容',
          key: 'content',
          width: TABLE_COLUMN_CONTENT_WIDTH
        },
        {
          title: '失败原因',
          key: 'reason',
          width: TABLE_COLUMN_REASON_WIDTH
        }
      ]
    }
  },
  computed: {
    dialogTitle() {
      const name = (this.row && this.row.playName) || ''
      return name ? `BP 同步失败 · ${name}` : 'BP 同步失败'
    },
    /** 仅使用接口 occurredAt 汇总；无则展示 — */
    failTimeText() {
      return this.latestFailTimeText || '—'
    },
    tableTotalWidth() {
      return TABLE_TOTAL_WIDTH
    },
    /** 仅数据行数超过上限时传入 height，否则表格高度随内容 */
    tableScrollBinding() {
      if (this.failItemList.length > MAX_ROWS_BEFORE_SCROLL) {
        return { height: TABLE_SCROLL_HEIGHT }
      }
      return {}
    }
  },
  watch: {
    visible(val) {
      this.dialogVisible = val
      if (val) {
        this.loadSyncFailures()
      } else {
        this.resetFailData()
      }
    },
    dialogVisible(val) {
      this.$emit('update:visible', val)
      if (!val) {
        this.resetFailData()
      }
    }
  },
  methods: {
    resetFailData() {
      this.failItemList = []
      this.latestFailTimeText = ''
      this.loading = false
    },
    loadSyncFailures() {
      const id = this.row && this.row.id
      if (id == null || id === '') {
        this.failItemList = []
        this.latestFailTimeText = ''
        return
      }
      this.loading = true
      getTtMediaReviewSyncFailures(id)
        .then((res) => {
          if (res.code === 200 && Array.isArray(res.data)) {
            this.failItemList = res.data.map(mapSyncFailureIssue)
            const latest = pickLatestOccurredAt(res.data)
            this.latestFailTimeText = latest
              ? latest.format('YYYY/MM/DD HH:mm:ss')
              : ''
          } else {
            this.failItemList = []
            this.latestFailTimeText = ''
            this.$Message.error(res.message || '获取同步失败明细失败')
          }
        })
        .catch(() => {
          this.failItemList = []
          this.latestFailTimeText = ''
          this.$Message.error('获取同步失败明细失败')
        })
        .finally(() => {
          this.loading = false
        })
    },
    handleClose() {
      this.dialogVisible = false
      this.$emit('close')
    }
  }
}
</script>

<style>
.bp-sync-fail-dialog .ivu-modal {
  width: 980px !important;
}

.bp-sync-fail-dialog .ivu-modal-content {
  width: 980px !important;
}

.bp-sync-fail-dialog .ivu-modal-header {
  height: 77px;
  padding: 0 24px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  border-bottom: 1px solid #e8eaec;
}

.bp-sync-fail-dialog .ivu-modal-header-inner {
  box-sizing: border-box;
  flex: 1;
  min-width: 0;
  max-width: 100%;
  padding-right: 48px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-family: Arial, 'Microsoft YaHei', sans-serif;
  font-size: 18px;
  font-weight: 600;
  color: #263f58;
  line-height: 24px;
}

.bp-sync-fail-dialog .ivu-modal-close {
  top: 22px;
  right: 20px;
  z-index: 1;
}

.bp-sync-fail-dialog .ivu-modal-body {
  padding: 20px 24px 12px;
}

.bp-sync-fail-body {
  width: 100%;
}

.bp-sync-fail-table-wrap {
  width: 100%;
  max-width: 100%;
  border: 1px solid #e8eaec;
  border-radius: 2px;
  box-sizing: border-box;
}

.bp-sync-fail-table.ivu-table-wrapper {
  max-width: 100%;
}

.bp-sync-fail-table .ivu-table-header th {
  height: 48px;
  background: #f8f8f9;
  font-weight: 600;
  font-size: 14px;
  color: #303133;
}

.bp-sync-fail-table .ivu-table td {
  vertical-align: middle;
  font-size: 14px;
  color: #303133;
  line-height: 1.5;
  height: 48px;
}

.bp-sync-fail-table .ivu-table-body {
  overflow: auto !important;
}

.bp-sync-fail-table .ivu-table-overflowX,
.bp-sync-fail-table .ivu-table-overflowY {
  overflow: auto !important;
}

.bp-sync-fail-time {
  margin-top: 20px;
  font-size: 14px;
  color: #909399;
  line-height: 1.5;
}

.bp-sync-fail-tip {
  margin: 8px 0 0;
  font-size: 13px;
  line-height: 1.6;
  color: #909399;
}

.bp-sync-fail-tip--secondary {
  margin-top: 4px;
}

.bp-sync-fail-dialog .ivu-modal-footer {
  height: 66px;
  padding: 0 24px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  border-top: 1px solid #e8eaec;
}
</style>
